import * as Sentry from '@sentry/react';
import type { ErrorEvent, EventHint, Integration } from '@sentry/core';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

/** Default targets so browser fetch/XHR to API, Hosting, Cloud Functions, and Cloud Run carry trace headers. */
const DEFAULT_TRACE_PROPAGATION_TARGETS: (string | RegExp)[] = [
  'localhost',
  /^https:\/\/api\.masonic-tools\.co\.uk/i,
  /^https:\/\/([a-z0-9-]+\.)?masonic-tools\.co\.uk/i,
  /^https:\/\/europe-west2[a-z0-9.-]*\.cloudfunctions\.net/i,
  /^https:\/\/[a-z0-9-]+-[a-z0-9-]+-[a-z0-9]+\.(?:a\.run\.app|europe-west2\.run\.app)/i,
];

export interface InitFrontendSentryOptions {
  dsn: string | undefined;
  environment: string;
  release?: string;
  tracesSampleRate: number;
  appName: string;
  tracePropagationTargets?: (string | RegExp)[];
  /** Session Replay (recommended for user-facing apps in Sentry React skill). Default true unless disabled. */
  enableReplay?: boolean;
  replaysSessionSampleRate?: number;
  replaysOnErrorSampleRate?: number;
  /** Enables Sentry.logger (opt-in; default false). */
  enableLogs?: boolean;
}

/**
 * Initialize the browser Sentry SDK for a Vite + React + React Router v6 app.
 * Load via `import './instrument'` as the first line in `main.tsx` (see Sentry AI skill).
 */
export function initFrontendSentry(options: InitFrontendSentryOptions): void {
  const dsn = options.dsn?.trim();
  if (!dsn) {
    return;
  }

  const enableReplay = options.enableReplay !== false;
  const integrations: Integration[] = [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ];
  if (enableReplay) {
    integrations.push(
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    );
  }

  Sentry.init({
    dsn,
    environment: options.environment,
    release: options.release,
    integrations,
    tracesSampleRate: options.tracesSampleRate,
    beforeSend: beforeSendDropFirebaseAuthNetworkFailure,
    tracePropagationTargets:
      options.tracePropagationTargets ?? DEFAULT_TRACE_PROPAGATION_TARGETS,
    replaysSessionSampleRate: enableReplay
      ? (options.replaysSessionSampleRate ?? 0.1)
      : 0,
    replaysOnErrorSampleRate: enableReplay
      ? (options.replaysOnErrorSampleRate ?? 1.0)
      : 0,
    enableLogs: options.enableLogs === true,
    sendDefaultPii: false,
    initialScope: {
      tags: { app: options.appName },
    },
  });
}

function isFirebaseAuthNetworkRequestFailed(hint: EventHint): boolean {
  const err = hint.originalException;
  if (err && typeof err === 'object' && 'code' in err) {
    return (err as { code?: string }).code === 'auth/network-request-failed';
  }
  return false;
}

function extractExceptionMessage(event: ErrorEvent, hint: EventHint): string {
  const fromHint =
    hint.originalException &&
    typeof hint.originalException === 'object' &&
    'message' in hint.originalException
      ? String((hint.originalException as { message?: unknown }).message ?? '')
      : hint.originalException
        ? String(hint.originalException)
        : '';

  if (fromHint) {
    return fromHint;
  }

  const fromEvent = event.exception?.values?.[0]?.value;
  return typeof fromEvent === 'string' ? fromEvent : '';
}

function isFirestoreListenBreadcrumb(
  breadcrumb: NonNullable<ErrorEvent['breadcrumbs']>[number],
): boolean {
  if (breadcrumb.category !== 'fetch' && breadcrumb.category !== 'http') {
    return false;
  }

  const maybeUrl = breadcrumb.data?.url;
  if (typeof maybeUrl !== 'string') {
    return false;
  }

  return maybeUrl.includes('google.firestore.v1.Firestore/Listen/channel');
}

function isSafariFirestoreLoadFailure(event: ErrorEvent, hint: EventHint): boolean {
  const message = extractExceptionMessage(event, hint).trim().toLowerCase();
  if (message !== 'load failed') {
    return false;
  }

  const mechanismType = event.exception?.values?.[0]?.mechanism?.type;
  if (mechanismType !== 'onunhandledrejection') {
    return false;
  }

  const breadcrumbs = event.breadcrumbs ?? [];
  return breadcrumbs.some(isFirestoreListenBreadcrumb);
}

/**
 * iOS/Safari can purge IndexedDB under storage pressure or in private mode. Firebase's internal
 * Installations/heartbeat service (@firebase/app) opens an IndexedDB it doesn't await or catch,
 * so the rejection surfaces as an unhandled promise rejection unrelated to our own Firestore/Auth
 * persistence config (which already avoids IndexedDB on iOS, see each app's src/lib/firebase.ts).
 */
function isFirebaseDatabaseDeletedByUser(event: ErrorEvent, hint: EventHint): boolean {
  const message = extractExceptionMessage(event, hint).trim().toLowerCase();
  if (!message.includes('database deleted by request of the user')) {
    return false;
  }
  const mechanismType = event.exception?.values?.[0]?.mechanism?.type;
  return mechanismType === 'onunhandledrejection';
}

/**
 * Microsoft's link scanners (Outlook Safe Links / Defender) open emailed URLs in a CefSharp
 * headless browser whose injected bridge rejects with a plain string such as
 * "Object Not Found Matching Id:8, MethodName:update, ParamCount:4". Not from our code.
 */
function isMicrosoftLinkScannerRejection(event: ErrorEvent, hint: EventHint): boolean {
  const message = extractExceptionMessage(event, hint);
  if (!/Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/.test(message)) {
    return false;
  }
  const mechanismType = event.exception?.values?.[0]?.mechanism?.type;
  return mechanismType === 'onunhandledrejection';
}

/** Drop Sentry events for transient Firebase Auth client timeouts (noisy on slow mobile networks). */
export function beforeSendDropFirebaseAuthNetworkFailure(
  event: ErrorEvent,
  hint: EventHint,
): ErrorEvent | null {
  if (isFirebaseAuthNetworkRequestFailed(hint)) {
    return null;
  }
  if (isSafariFirestoreLoadFailure(event, hint)) {
    return null;
  }
  if (isFirebaseDatabaseDeletedByUser(event, hint)) {
    return null;
  }
  if (isMicrosoftLinkScannerRejection(event, hint)) {
    return null;
  }
  return event;
}

export function parseTracesSampleRate(raw: string | undefined, defaultValue: number): number {
  if (raw === undefined || raw === '') {
    return defaultValue;
  }
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    return defaultValue;
  }
  return Math.min(1, Math.max(0, n));
}
